import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { RoleTemplatesAPI } from "@/lib/sprint3-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Shield, Lock, Plus, Trash2, Pencil, LayoutGrid, Grid3x3 } from "lucide-react";
import PermissionMatrix from "@/components/roles/PermissionMatrix";

const ADMIN_ROLES = new Set(["super_admin", "inst_admin"]);

export default function RoleTemplates() {
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some((r) => ADMIN_ROLES.has(r));
  const isSuper = (user?.roles || []).includes("super_admin");
  const qc = useQueryClient();
  const tplQuery = useQuery({ queryKey: ["role-templates"], queryFn: () => RoleTemplatesAPI.list() });
  const permQuery = useQuery({ queryKey: ["permissions"], queryFn: () => RoleTemplatesAPI.permissions() });
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("cards"); // 'cards' | 'matrix'

  const remove = async (t) => {
    if (!window.confirm(`Delete custom role "${t.name}"?`)) return;
    try {
      await RoleTemplatesAPI.remove(t.id);
      toast.success("Custom role removed");
      qc.invalidateQueries({ queryKey: ["role-templates"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <AppShell>
      <div className="p-8 max-w-6xl mx-auto" data-testid="role-templates-page">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl">Role templates</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              {permQuery.data?.permissions?.length || 0} fine-grained permissions. Create your own
              roles on top of the {tplQuery.data?.filter((t) => t.is_system).length || 11} built-in ones.
            </p>
          </div>
          {canEdit && permQuery.data && (
            <CreateTemplateDialog permissions={permQuery.data.permissions} onDone={() => qc.invalidateQueries({ queryKey: ["role-templates"] })} />
          )}
        </div>

        <div className="flex items-center gap-1 mb-5 bg-card rounded-xl shadow-sm p-1 w-fit" data-testid="role-view-toggle">
          <Button
            size="sm"
            variant={view === "cards" ? "secondary" : "ghost"}
            onClick={() => setView("cards")}
            className="h-7 px-3 text-xs"
            data-testid="view-cards-btn"
          >
            <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />Cards
          </Button>
          <Button
            size="sm"
            variant={view === "matrix" ? "secondary" : "ghost"}
            onClick={() => setView("matrix")}
            className="h-7 px-3 text-xs"
            data-testid="view-matrix-btn"
          >
            <Grid3x3 className="h-3.5 w-3.5 mr-1.5" />Permission Matrix
          </Button>
        </div>

        {view === "matrix" ? (
          <PermissionMatrix />
        ) : tplQuery.isLoading ? (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="role-templates-grid">
            {(tplQuery.data || [])
              .slice()
              .sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name))
              .map((t) => (
              <div
                key={t.id}
                className="bg-card rounded-2xl shadow-sm p-4 hover:shadow-md transition-shadow"
                data-testid={`role-template-${t.key}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.is_system ? <Lock className="h-3.5 w-3.5 text-muted-foreground" /> : <Shield className="h-3.5 w-3.5 text-primary" />}
                      <h3 className="font-display font-bold text-base leading-none truncate">{t.name}</h3>
                      <Badge variant="outline" className="font-mono text-[9px] h-4 px-1.5">L{t.level ?? 0}</Badge>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mt-1">
                      {t.key}{t.is_system ? " · system" : " · custom"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className="font-mono text-[10px]">{t.permissions.length} perms</Badge>
                    {canEdit && (t.is_system ? isSuper : true) && (
                      <Button size="icon" variant="ghost" onClick={() => setEditing(t)} title="Edit permissions" data-testid={`role-template-edit-${t.key}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {!t.is_system && canEdit && (
                      <Button size="icon" variant="ghost" onClick={() => remove(t)} title="Delete custom role" data-testid={`role-template-delete-${t.key}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
                {t.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{t.description}</p>}
                <div className="flex flex-wrap gap-1">
                  {t.permissions.slice(0, 18).map((p) => (
                    <code key={p} className="text-[10px] font-mono bg-secondary px-1.5 py-0.5 rounded">{p}</code>
                  ))}
                  {t.permissions.length > 18 && (
                    <code className="text-[10px] font-mono text-muted-foreground">+{t.permissions.length - 18}</code>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && permQuery.data && (
          <EditTemplateDialog
            template={editing}
            permissions={permQuery.data.permissions}
            onClose={() => setEditing(null)}
            onDone={() => {
              setEditing(null);
              qc.invalidateQueries({ queryKey: ["role-templates"] });
            }}
          />
        )}
      </div>
    </AppShell>
  );
}

function CreateTemplateDialog({ permissions, onDone }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const groups = permissions.reduce((acc, p) => {
    const g = p.key.split(".")[0];
    acc[g] = acc[g] || [];
    acc[g].push(p);
    return acc;
  }, {});

  const togglePerm = (k) => {
    const n = new Set(picked);
    if (n.has(k)) n.delete(k); else n.add(k);
    setPicked(n);
  };
  const toggleGroup = (g, all) => {
    const n = new Set(picked);
    if (all) groups[g].forEach((p) => n.add(p.key));
    else groups[g].forEach((p) => n.delete(p.key));
    setPicked(n);
  };

  const submit = async () => {
    const slug = key.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_");
    if (slug.length < 2) return toast.error("Key must be 2+ characters");
    if (!name.trim()) return toast.error("Name is required");
    if (picked.size === 0) return toast.error("Pick at least one permission");
    setBusy(true);
    try {
      await RoleTemplatesAPI.create({ key: slug, name: name.trim(), description: description.trim() || undefined, permissions: Array.from(picked) });
      toast.success("Custom role created");
      setKey(""); setName(""); setDescription(""); setPicked(new Set());
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-role-template-btn"><Plus className="h-4 w-4 mr-1.5" /> New custom role</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="create-role-template-dialog">
        <DialogHeader>
          <DialogTitle>Create custom role</DialogTitle>
          <DialogDescription>Pick a unique key, name, and the permissions this role grants.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Key (slug)</Label>
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="e.g. exam_coordinator" data-testid="rt-key-input" />
          </div>
          <div>
            <Label className="text-xs">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Exam Coordinator" data-testid="rt-name-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short note about what this role does…" data-testid="rt-desc-input" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Permissions ({picked.size} selected)</Label>
          </div>
          <div className="bg-card rounded-xl shadow-sm divide-y divide-border max-h-72 overflow-auto" data-testid="rt-permissions-grid">
            {Object.entries(groups).map(([g, items]) => {
              const allOn = items.every((p) => picked.has(p.key));
              return (
                <div key={g} className="p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{g}</div>
                    <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => toggleGroup(g, !allOn)} data-testid={`rt-group-toggle-${g}`}>
                      {allOn ? "Unselect group" : "Select group"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {items.map((p) => (
                      <label key={p.key} className="flex items-start gap-2 text-xs cursor-pointer">
                        <Checkbox checked={picked.has(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`rt-perm-${p.key}`} />
                        <div className="min-w-0">
                          <code className="font-mono">{p.key}</code>
                          <div className="text-muted-foreground truncate">{p.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="rt-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Create role</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditTemplateDialog({ template, permissions, onClose, onDone }) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [picked, setPicked] = useState(new Set(template.permissions));
  const [busy, setBusy] = useState(false);
  const isSystem = template.is_system;

  const groups = permissions.reduce((acc, p) => {
    const g = p.key.split(".")[0];
    acc[g] = acc[g] || [];
    acc[g].push(p);
    return acc;
  }, {});

  const togglePerm = (k) => {
    const n = new Set(picked);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setPicked(n);
  };
  const toggleGroup = (g, all) => {
    const n = new Set(picked);
    if (all) groups[g].forEach((p) => n.add(p.key));
    else groups[g].forEach((p) => n.delete(p.key));
    setPicked(n);
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (picked.size === 0) return toast.error("Pick at least one permission");
    setBusy(true);
    try {
      await RoleTemplatesAPI.update(template.id, {
        name: name.trim(),
        description: description.trim() || null,
        permissions: Array.from(picked),
      });
      toast.success(`${isSystem ? "System role" : "Role"} permissions updated`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="edit-role-template-dialog">
        <DialogHeader>
          <DialogTitle>Edit {isSystem ? "system" : "custom"} role — {template.key}</DialogTitle>
          <DialogDescription>
            {isSystem
              ? "Changes here override the built-in defaults and take effect for new requests immediately."
              : "Update the name, description and permission set for this custom role."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Display name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-rt-name" />
          {isSystem && (
            <div className="text-[11px] text-muted-foreground mt-1">
              The internal key <code className="font-mono">{template.key}</code> stays the same — only the display label changes.
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="edit-rt-desc" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Permissions ({picked.size} selected)</Label>
          </div>
          <div className="bg-card rounded-xl shadow-sm divide-y divide-border max-h-72 overflow-auto" data-testid="edit-rt-permissions">
            {Object.entries(groups).map(([g, items]) => {
              const allOn = items.every((p) => picked.has(p.key));
              return (
                <div key={g} className="p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{g}</div>
                    <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => toggleGroup(g, !allOn)} data-testid={`edit-rt-group-${g}`}>
                      {allOn ? "Unselect group" : "Select group"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {items.map((p) => (
                      <label key={p.key} className="flex items-start gap-2 text-xs cursor-pointer">
                        <Checkbox checked={picked.has(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`edit-rt-perm-${p.key}`} />
                        <div className="min-w-0">
                          <code className="font-mono">{p.key}</code>
                          <div className="text-muted-foreground truncate">{p.label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-rt-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

